/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2024, Zumper
 * @description FAQSection
 */

import { Flex, Heading, Link, Text } from '@chakra-ui/react'
import { FAQ_IMPRESSION } from '@zcode-ui/web-zumper-integrations'
import { HOMEPAGE } from '@zcode/mixpanel'
import * as React from 'react'
import { useHomepageImpression } from '../hooks/useHomepageImpression'

const FAQSection = () => {
  const ref = useHomepageImpression({ name: FAQ_IMPRESSION, mixpanelSection: HOMEPAGE.FAQ })
  return (
    <Flex
      flexDir={{ base: 'column', desktop: 'row' }}
      justifyContent={{ desktop: 'space-between' }}
      gap={10}
      ref={ref}
    >
      <Flex flexDir="column" gap={{ base: 4, desktop: 3 }} maxWidth={{ lg: '279px' }}>
        <Heading variant="display28To32">
          Questions?
          <br />
          We've got answers.
        </Heading>
        <Text color="gray.950" variant="body16Md">
          Something else to ask?{' '}
          <Link color="blue.800" href="https://help.zumper.com/hc/en-us/requests/new">
            Contact us
          </Link>
        </Text>
      </Flex>
      <Flex flexDir="column" gap={10} maxWidth={{ lg: 380, xl: 588 }}>
        <Flex flexDir="column" gap={4}>
          <Heading<'h3'> as="h3" variant="display20To24">
            What is Zumper?
          </Heading>
          <Text color="gray.950" variant="body16Reg">
            Zumper is a digital marketplace for renters and property managers. For renters, we
            deliver best-in-class search across our website and mobile apps, with the most
            comprehensive database of active rental listings in the U.S. For property managers, we
            offer marketing solutions to efficiently fill vacancies with quality renters. Learn more
            about{' '}
            <Link href="/about" color="blue.800">
              our mission
            </Link>{' '}
            to make renting a home as easy as booking a hotel.
          </Text>
        </Flex>
        <Flex flexDir="column" gap={4}>
          <Heading<'h3'> as="h3" variant="display20To24">
            Who owns Zumper?
          </Heading>
          <Text color="gray.950" variant="body16Reg">
            Zumper is the largest privately owned rental platform in North America and has raised
            $180 million from investors that include Kleiner Perkins, Goodwater Capital, Headline,
            Dawn Capital, and the Blackstone Group.
          </Text>
        </Flex>
        <Flex flexDir="column" gap={4}>
          <Heading<'h3'> as="h3" variant="display20To24">
            Do I need to pay to use Zumper?
          </Heading>
          <Text color="gray.950" variant="body16Reg">
            Renters can use our platform to browse or contact properties for free. Zumper will not
            ask you for money to view or communicate with rental properties listed on our site.
            Property owners and managers should visit our{' '}
            <Link color="blue.800" href="https://advertise.zumper.com">
              Advertise page
            </Link>{' '}
            for more information about potential costs for advertising their property on Zumper.
          </Text>
        </Flex>
        <Flex flexDir="column" gap={4}>
          <Heading<'h3'> as="h3" variant="display20To24">
            How does Zumper make sure that I'm not seeing scams?
          </Heading>
          <Text color="gray.950" variant="body16Reg">
            We take your security seriously. We carefully review properties listed on our site to
            ensure we're offering you a secure experience. Look for the "verified" tag on
            listings—these are properties we have confirmed are posted by real people with real
            apartments available for rent. Be cautious when providing personal or financial
            information, or making payments to someone you don't know. We encourage you to
            familiarize yourself with{' '}
            <Link
              href="https://help.zumper.com/hc/en-us/articles/8731474628251-Avoid-Rental-Scams-and-Fraud"
              color="blue.800"
            >
              common scams and red flags
            </Link>
            , and if you see something suspicious, please contact us at{' '}
            <Link href="mailto:support@zumper.com" color="blue.800">
              support@zumper.com
            </Link>
            .
          </Text>
        </Flex>
        <Flex flexDir="column" gap={4}>
          <Heading<'h3'> as="h3" variant="display20To24">
            How do you post a listing to Zumper?
          </Heading>
          <Text color="gray.950" variant="body16Reg">
            Visit our{' '}
            <Link href="https://advertise.zumper.com" color="blue.800">
              Advertise
            </Link>{' '}
            page. Whether you're a property owner, manager, or broker, we'll send you to the right
            place to get you started.
          </Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default FAQSection
